<script setup lang="ts">
  import { countryFormats } from '~/helpers/countryFormats'
  import { object, array } from 'yup'
  import type { IUpdateUserData } from './types'

  const emit = defineEmits<{
    (e: 'update:modelValue', value: IUpdateUserData): void
  }>()

  const props = defineProps<{
    modelValue: IUpdateUserData
    items: Array<{ id: string; value: string; label: string }>
    isEditing: boolean
  }>()

  const { t } = useI18n()
  const form = ref()
  const infoRef = ref()
  const fileUploader = ref()

  const { phoneSchema, nameSchema, surnameSchema, middlenameSchema } =
    useValidationSchemas()

  const schema = computed(() => {
    const contactSchema =
      infoRef.value?.getSchema()?.fields.contacts || array().of(object())

    return object().shape({
      name: nameSchema,
      surname: surnameSchema,
      middlename: middlenameSchema,
      phone: phoneSchema,
      contacts: contactSchema,
    })
  })

  const ContactForm = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  })

  defineExpose({
    validate: async () => {
      try {
        const contactsValidation = await infoRef.value?.validate()
        if (!contactsValidation?.valid) {
          throw contactsValidation?.errors
        }

        await form.value.validate()

        const fileValidation = await fileUploader.value?.validate()
        if (fileValidation && !fileValidation.valid) {
          throw fileValidation.error
        }

        return { valid: true }
      } catch (error) {
        return { valid: false, error }
      }
    },
  })
</script>

<template>
  <UForm
    ref="form"
    :schema="schema"
    :state="ContactForm"
    class="flex w-full flex-col gap-2"
  >
    <UFormField :label="t('pages.me.update-user-data.name-title')" name="name">
      <Input
        v-model="ContactForm.name"
        :placeholder="t('pages.me.update-user-data.name')"
        type="text"
      />
    </UFormField>

    <UFormField
      :label="t('pages.me.update-user-data.middlename-title')"
      name="middlename"
    >
      <Input
        v-model="ContactForm.middlename"
        :placeholder="t('pages.me.update-user-data.middlename')"
        type="text"
      />
    </UFormField>

    <UFormField
      :label="t('pages.me.update-user-data.surname-title')"
      name="surname"
    >
      <Input
        v-model="ContactForm.surname"
        :placeholder="t('pages.me.update-user-data.surname')"
        type="text"
      />
    </UFormField>

    <UFormField :label="t('pages.me.update-user-data.email')" name="email">
      <Input
        disabled
        v-model="ContactForm.email"
        :placeholder="t('pages.me.update-user-data.surname')"
        type="text"
      />
    </UFormField>

    <UFormField
      :label="t('pages.me.update-user-data.phone-title')"
      name="phone"
    >
      <PhoneNumberInput
        v-model="ContactForm.phone"
        class="w-full"
        :country-formats="countryFormats"
        :placeholder="t('pages.me.update-user-data.phone')"
      />
    </UFormField>

    <span>{{ t('pages.me.contact-information.title') }}</span>

    <ContactInformationForm
      ref="infoRef"
      v-model="ContactForm.contacts"
      :items="items"
      :is-editing="isEditing"
    />

    <UFormField
      :label="t('pages.me.update-user-data.avatar-title')"
      name="file"
    >
      <FileUploader
        v-model="ContactForm.file"
        ref="fileUploader"
        typeFiles="img"
        folder="avatar"
      />
    </UFormField>
  </UForm>
</template>
